"use client";
import { useState, useEffect } from "react";
import { SignInModal } from "./SignInModal";
import { EditNameModal } from "./EditNameModal";
import { useAuth } from "@/store/auth";

interface AuthFlowProps {
  open: boolean;
  onClose: () => void;
}

/**
 * Handles the end-to-end authentication flow.
 * 1. Shows SignInModal for phone/OTP verification.
 * 2. If the user is new (has no name), automatically shows EditNameModal.
 */
export function AuthFlow({ open, onClose }: AuthFlowProps) {
  const { isLoggedIn, user } = useAuth();
  const [showNameModal, setShowNameModal] = useState(false);
  const [internalOpen, setInternalOpen] = useState(open);

  useEffect(() => {
    setInternalOpen(open);
    if (!open) {
      setShowNameModal(false);
    }
  }, [open]);


  // Watch for login success to decide whether to show the name modal.
  // We use a small effect to catch the transition from logged-out to logged-in.
  useEffect(() => {
    if (isLoggedIn && internalOpen && !user?.name) {
      setShowNameModal(true);
    }
  }, [isLoggedIn, internalOpen, user?.name]);

  const handleSignInClose = () => {
    onClose();
  };

  const handleSignInSuccess = () => {
    // We check if the user has a name. 
    // If no name, we switch to the name modal.
    if (!user?.name) {
      setShowNameModal(true);
    } else {
      // If name already exists, we're done.
      onClose();
    }
  };

  if (!internalOpen) return null;

  if (showNameModal) {
    return (
      <EditNameModal
        open={true}
        onClose={onClose}
        onSuccess={onClose}
        title="Welcome!"
        subtitle="Please enter your name to complete your profile."
      />
    );
  }

  return (
    <SignInModal
      open={internalOpen}
      onClose={handleSignInClose}
      onSuccess={handleSignInSuccess}
    />
  );
}
